import BracketPlayer, { isSamePlayer } from './BracketPlayer';
import {
  getMatchDisplayNumber,
  getMatchPlayerScore,
  getRound1SlotIndex,
  getRoundMatchNumberRange,
  isMatchDecided,
} from '../../utils/bracket';
import { useI18n } from '../../i18n';
import { roundLabelFromSize, translateRoundLabel } from '../../i18n/labels';

export const CENTER_LAYOUT_MIN_BRACKET_SIZE = 16;

function MatchNumberBadge({ number }) {
  const { t } = useI18n();
  return (
    <span className="match-number" aria-label={t('bracket.matchNoAria', { no: number })}>
      {t('bracket.matchNo', { no: number })}
    </span>
  );
}

/** 경기 칸 1개 (1:1 경기 목록에서도 쓴다). emptyText: 선수가 없는 자리 문구 */
export function BracketMatch({
  bracket,
  match,
  roundIndex,
  matchIndex,
  bracketSize,
  editMode,
  selectedSwapSlot,
  onSelectWinner,
  onSelectRound1Slot,
  onEditScore,
  liveMatches,
  onStartLive,
  onEndLive,
  emptyText,
}) {
  const { t } = useI18n();
  const matchNumber = getMatchDisplayNumber(bracket, roundIndex, matchIndex);
  const hasPlayer1 = Boolean(match.player1);
  const hasPlayer2 = Boolean(match.player2);
  const isRound1 = roundIndex === 0;
  const canEditRound1 = editMode && isRound1 && onSelectRound1Slot;

  const slot1 = getRound1SlotIndex(bracketSize, matchIndex, true);
  const slot2 = getRound1SlotIndex(bracketSize, matchIndex, false);

  const handleSelect = (player, isPlayer1) => {
    if (canEditRound1) {
      onSelectRound1Slot(isPlayer1 ? slot1 : slot2);
      return;
    }
    if (!player || !onSelectWinner) return;
    onSelectWinner(roundIndex, matchIndex, player);
  };

  if (!hasPlayer1 && !hasPlayer2) {
    return (
      <div className="match match-pending">
        <MatchNumberBadge number={matchNumber} />
        <div className="player tbd">{emptyText ?? t('bracket.winnerAdvances')}</div>
        <div className="vs-line" />
        <div className="player tbd">{emptyText ?? t('bracket.winnerAdvances')}</div>
      </div>
    );
  }

  const canSelectWinner = canEditRound1 || Boolean(onSelectWinner);
  const player1Clickable = hasPlayer1 && canSelectWinner;
  const player2Clickable = hasPlayer2 && canSelectWinner;
  const hasWinner = !editMode && Boolean(match.winner);
  const canEditScore = !editMode && onEditScore && hasPlayer1 && hasPlayer2;
  const hasScores = Object.keys(match.scores ?? {}).length > 0;
  const live = editMode
    ? null
    : liveMatches?.find((l) => l.roundIndex === roundIndex && l.matchIndex === matchIndex);
  const isLive = Boolean(live);
  const isDraw = !editMode && Boolean(match.draw);
  const canStartLive = canEditScore && onStartLive && !isLive && !isMatchDecided(match);
  const canEndLive = isLive && onEndLive;

  return (
    <div
      className={`match${canEditRound1 ? ' match-editable' : ''}${isLive ? ' match-live' : ''}`}
    >
      <MatchNumberBadge number={matchNumber} />
      {isLive && (
        <span className="match-live-badge">● {t('bracket.live.court', { court: live.court })}</span>
      )}
      <BracketPlayer
        player={match.player1}
        score={editMode ? null : getMatchPlayerScore(match, match.player1)}
        isWinner={!editMode && isSamePlayer(match.winner, match.player1)}
        isLoser={hasWinner && hasPlayer1 && !isSamePlayer(match.winner, match.player1)}
        isDraw={isDraw}
        isClickable={player1Clickable}
        isSwapSelected={canEditRound1 && selectedSwapSlot === slot1}
        isEditMode={canEditRound1}
        onSelect={() => handleSelect(match.player1, true)}
        emptyText={emptyText}
      />
      <div className="vs-line" />
      <BracketPlayer
        player={match.player2}
        score={editMode ? null : getMatchPlayerScore(match, match.player2)}
        isWinner={!editMode && isSamePlayer(match.winner, match.player2)}
        isLoser={hasWinner && hasPlayer2 && !isSamePlayer(match.winner, match.player2)}
        isDraw={isDraw}
        isClickable={player2Clickable}
        isSwapSelected={canEditRound1 && selectedSwapSlot === slot2}
        isEditMode={canEditRound1}
        onSelect={() => handleSelect(match.player2, false)}
        emptyText={emptyText}
      />
      {(canEditScore || canEndLive) && (
        <div className="match-actions">
          {canStartLive && (
            <button
              type="button"
              className="match-live-btn"
              onClick={() => onStartLive(roundIndex, matchIndex)}
            >
              {t('bracket.startMatch')}
            </button>
          )}
          {canEndLive && (
            <button
              type="button"
              className="match-live-btn match-live-btn-end"
              onClick={() => onEndLive(live.court)}
            >
              {t('bracket.endMatch')}
            </button>
          )}
          {canEditScore && (
            <button
              type="button"
              className="match-score-btn"
              onClick={() => onEditScore(roundIndex, matchIndex)}
            >
              {hasScores ? t('bracket.editScore') : t('bracket.enterScore')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function getRoundMatchEntries(round, side) {
  const total = round.matches.length;
  if (!side || side === 'full' || total <= 1) {
    return round.matches.map((match, matchIndex) => ({ match, matchIndex }));
  }

  const half = Math.ceil(total / 2);

  if (side === 'left') {
    return round.matches
      .slice(0, half)
      .map((match, matchIndex) => ({ match, matchIndex }));
  }

  const rightMatches = round.matches.slice(half);
  return rightMatches
    .map((match, idx) => ({
      match,
      matchIndex: half + idx,
    }))
    .reverse();
}

function BracketRound({
  bracket,
  round,
  side,
  bracketSize,
  editMode,
  selectedSwapSlot,
  onSelectWinner,
  onSelectRound1Slot,
  onEditScore,
  liveMatches,
  onStartLive,
  onEndLive,
}) {
  const { t } = useI18n();
  const entries = getRoundMatchEntries(round, side);
  const isRight = side === 'right';
  const roundRange = getRoundMatchNumberRange(bracket, round.roundIndex);
  const roundTitleExtra =
    roundRange && roundRange.count > 1
      ? t('bracket.roundRange', { start: roundRange.start, end: roundRange.end })
      : roundRange
        ? t('bracket.roundRangeSingle', { start: roundRange.start })
        : '';

  return (
    <div className={`round${isRight ? ' round-right' : ''}`}>
      <div className="round-title">
        {round.roundIndex != null && bracket.bracketSize
          ? roundLabelFromSize(bracket.bracketSize, round.roundIndex)
          : translateRoundLabel(round.title)}
        <span className="round-title-range">{roundTitleExtra}</span>
      </div>
      <div className="matchups">
        {entries.map(({ match, matchIndex }) => (
          <BracketMatch
            key={`${round.roundIndex}-${matchIndex}`}
            bracket={bracket}
            match={match}
            roundIndex={round.roundIndex}
            matchIndex={matchIndex}
            bracketSize={bracketSize}
            editMode={editMode}
            selectedSwapSlot={selectedSwapSlot}
            onSelectWinner={onSelectWinner}
            onSelectRound1Slot={onSelectRound1Slot}
            onEditScore={onEditScore}
            liveMatches={liveMatches}
            onStartLive={onStartLive}
            onEndLive={onEndLive}
          />
        ))}
      </div>
    </div>
  );
}

function BracketChampion({ champion }) {
  const { t } = useI18n();
  return (
    <div className="round round-champion">
      <div className="round-title">{t('bracket.champion')}</div>
      <div className="matchups">
        <div className={`champion-slot${champion ? ' filled' : ''}`}>
          <span className="trophy-icon">🏆</span>
          {champion ? (
            <>
              <div className="champion-name">{champion.participantName || champion.name}</div>
              <div className="champion-detail">{champion.gymName ?? t('common.noGym')}</div>
            </>
          ) : (
            'CHAMPION'
          )}
        </div>
      </div>
    </div>
  );
}

function BracketLinearLayout({
  bracket,
  editMode,
  selectedSwapSlot,
  onSelectWinner,
  onSelectRound1Slot,
  onEditScore,
  liveMatches,
  onStartLive,
  onEndLive,
  champion,
}) {
  return (
    <div className="bracket bracket-linear">
      {bracket.rounds.map((round) => (
        <BracketRound
          key={round.title}
          bracket={bracket}
          round={round}
          side="full"
          bracketSize={bracket.bracketSize}
          editMode={editMode}
          selectedSwapSlot={selectedSwapSlot}
          onSelectWinner={onSelectWinner}
          onSelectRound1Slot={onSelectRound1Slot}
          onEditScore={onEditScore}
          liveMatches={liveMatches}
          onStartLive={onStartLive}
          onEndLive={onEndLive}
        />
      ))}
      <BracketChampion champion={champion} />
    </div>
  );
}

function BracketCenterLayout({
  bracket,
  editMode,
  selectedSwapSlot,
  onSelectWinner,
  onSelectRound1Slot,
  onEditScore,
  liveMatches,
  onStartLive,
  onEndLive,
  champion,
}) {
  const prelimRounds = bracket.rounds.slice(0, -1);
  const finalRound = bracket.rounds[bracket.rounds.length - 1];

  return (
    <div className="bracket bracket-center">
      <div className="bracket-side bracket-side-left">
        {prelimRounds.map((round) => (
          <BracketRound
            key={`left-${round.title}`}
            bracket={bracket}
            round={round}
            side="left"
            bracketSize={bracket.bracketSize}
            editMode={editMode}
            selectedSwapSlot={selectedSwapSlot}
            onSelectWinner={onSelectWinner}
            onSelectRound1Slot={onSelectRound1Slot}
            onEditScore={onEditScore}
            liveMatches={liveMatches}
            onStartLive={onStartLive}
            onEndLive={onEndLive}
          />
        ))}
      </div>

      <div className="bracket-center-core">
        <BracketRound
          bracket={bracket}
          round={finalRound}
          side="full"
          bracketSize={bracket.bracketSize}
          editMode={editMode}
          selectedSwapSlot={selectedSwapSlot}
          onSelectWinner={onSelectWinner}
          onSelectRound1Slot={onSelectRound1Slot}
          onEditScore={onEditScore}
          liveMatches={liveMatches}
          onStartLive={onStartLive}
          onEndLive={onEndLive}
        />
        <BracketChampion champion={champion} />
      </div>

      <div className="bracket-side bracket-side-right">
        {[...prelimRounds].reverse().map((round) => (
          <BracketRound
            key={`right-${round.title}`}
            bracket={bracket}
            round={round}
            side="right"
            bracketSize={bracket.bracketSize}
            editMode={editMode}
            selectedSwapSlot={selectedSwapSlot}
            onSelectWinner={onSelectWinner}
            onSelectRound1Slot={onSelectRound1Slot}
            onEditScore={onEditScore}
            liveMatches={liveMatches}
            onStartLive={onStartLive}
            onEndLive={onEndLive}
          />
        ))}
      </div>
    </div>
  );
}

function BracketView({
  bracket,
  onSelectWinner,
  editMode = false,
  selectedSwapSlot = null,
  onSelectRound1Slot,
  onEditScore,
  liveMatches,
  onStartLive,
  onEndLive,
}) {
  if (!bracket) return null;

  const champion = bracket.champion;
  const useCenterLayout = bracket.bracketSize >= CENTER_LAYOUT_MIN_BRACKET_SIZE;
  const layoutProps = {
    bracket,
    editMode,
    selectedSwapSlot,
    onSelectWinner: editMode ? undefined : onSelectWinner,
    onSelectRound1Slot,
    onEditScore: editMode ? undefined : onEditScore,
    liveMatches: editMode ? null : liveMatches,
    onStartLive: editMode ? undefined : onStartLive,
    onEndLive: editMode ? undefined : onEndLive,
    champion,
  };

  if (useCenterLayout) {
    return <BracketCenterLayout {...layoutProps} />;
  }

  return <BracketLinearLayout {...layoutProps} />;
}

export default BracketView;
